import { useCallback, useEffect, useState, type FormEvent } from "react";
import type { RoleSlug } from "@nxvolt/shared";
import { useAuth } from "../lib/auth-context.js";

interface UserRow {
  id: number;
  email: string;
  displayName: string;
  role: RoleSlug;
  active: boolean;
  mustChangePassword: boolean;
  createdAt: string;
  lastLoginAt: string | null;
  garageScope: "all" | "restricted";
  garageIds: number[];
}

interface GarageRow {
  id: number;
  code: string;
  name: string;
  active: boolean;
}

const ROLE_LABELS: Record<RoleSlug, string> = {
  admin: "Administrador",
  supervisor: "Supervisor",
  operator: "Operador",
  viewer: "Visualização",
};

function fmt(iso: string | null) {
  return iso
    ? new Date(iso).toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";
}

export function UsersPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState<UserRow[]>([]);
  const [garages, setGarages] = useState<GarageRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [newGarageScope, setNewGarageScope] = useState<"all" | "restricted">("all");
  const [newGarageIds, setNewGarageIds] = useState<number[]>([]);
  const [tempCred, setTempCred] = useState<{
    email: string;
    password: string;
  } | null>(null);

  const load = useCallback(() => {
    fetch("/api/users", { credentials: "include" })
      .then((r) =>
        r.ok ? r.json() : Promise.reject(new Error(String(r.status))),
      )
      .then((d: { users: UserRow[] }) => setUsers(d.users))
      .catch((e: Error) => setError(e.message));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    fetch("/api/garages", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((d: { items: GarageRow[] }) => setGarages(d.items.filter((g) => g.active)))
      .catch((e: Error) => setError(e.message));
  }, []);

  async function create(e: FormEvent) {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;
    const fd = new FormData(form);
    if (newGarageScope === "restricted" && newGarageIds.length === 0) {
      alert("Selecione ao menos uma unidade para acesso restrito.");
      return;
    }
    const res = await fetch("/api/users", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email: fd.get("email"),
        displayName: fd.get("displayName"),
        role: fd.get("role"),
        garageScope: newGarageScope,
        garageIds: newGarageIds,
      }),
    });
    if (!res.ok) {
      const b = (await res.json().catch(() => ({}))) as { error?: string };
      if (b.error === "email_in_use") alert("E-mail já cadastrado.");
      else alert("Erro: " + (b.error ?? res.status));
      return;
    }
    const body = (await res.json()) as {
      user: UserRow;
      temporaryPassword?: string;
    };
    setTempCred(
      body.temporaryPassword
        ? { email: body.user.email, password: body.temporaryPassword }
        : null,
    );
    form.reset();
    setNewGarageScope("all");
    setNewGarageIds([]);
    setShowForm(false);
    load();
  }

  async function patch(id: number, patchBody: Record<string, unknown>) {
    const res = await fetch(`/api/users/${id}`, {
      method: "PATCH",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patchBody),
    });
    if (!res.ok) {
      const b = (await res.json().catch(() => ({}))) as { error?: string };
      const msg =
        {
          last_admin_protection: "Não pode remover o último admin ativo.",
          cannot_deactivate_self: "Você não pode desativar a si mesmo.",
        }[b.error ?? ""] ?? "Erro: " + (b.error ?? res.status);
      alert(msg);
      return;
    }
    const body = (await res.json()) as {
      user: UserRow;
      temporaryPassword?: string;
    };
    if (body.temporaryPassword)
      setTempCred({ email: body.user.email, password: body.temporaryPassword });
    load();
  }

  async function toggleActive(u: UserRow) {
    if (u.active && !confirm(`Desativar ${u.email}?`)) return;
    await patch(u.id, { active: !u.active });
  }

  async function updateUnitScope(u: UserRow, garageScope: "all" | "restricted") {
    if (garageScope === "restricted" && u.garageIds.length === 0) {
      alert("Selecione uma unidade antes de restringir o acesso.");
      return;
    }
    await patch(u.id, { garageScope, garageIds: u.garageIds });
  }

  async function toggleGarage(u: UserRow, garageId: number, checked: boolean) {
    const garageIds = checked
      ? [...new Set([...u.garageIds, garageId])]
      : u.garageIds.filter((id) => id !== garageId);
    if (garageIds.length === 0) {
      alert("Uma conta restrita precisa ter ao menos uma unidade.");
      return;
    }
    await patch(u.id, { garageScope: "restricted", garageIds });
  }

  function toggleNewGarage(garageId: number, checked: boolean) {
    setNewGarageIds((ids) =>
      checked ? [...new Set([...ids, garageId])] : ids.filter((id) => id !== garageId),
    );
  }

  function unitLabel(u: UserRow) {
    if (u.role === "admin" || u.garageScope === "all") return "Todas as unidades";
    const names = garages
      .filter((garage) => u.garageIds.includes(garage.id))
      .map((garage) => garage.name);
    return names.length > 0 ? names.join(", ") : "Nenhuma unidade";
  }

  async function resetPwd(u: UserRow) {
    if (
      !confirm(
        `Resetar senha de ${u.email}? A nova senha aparecerá na tela e você precisa entregá-la ao usuário.`,
      )
    )
      return;
    await patch(u.id, { resetPassword: true });
  }

  const activeCount = users.filter((u) => u.active).length;
  const sub = `${activeCount} ativo${activeCount === 1 ? "" : "s"} · ${users.length} no total`;

  return (
    <div>
      <div className="nx-page-head">
        <div className="nx-page-head__title">
          <span className="nx-page-head__eyebrow">Administração</span>
          <h1>Usuários</h1>
          <div className="nx-page-head__sub">{sub}</div>
        </div>
        <div className="nx-page-head__actions">
          <button
            className="nx-btn"
            type="button"
            onClick={() => setShowForm((s) => !s)}
          >
            {showForm ? "Cancelar" : "+ Novo usuário"}
          </button>
        </div>
      </div>

      {tempCred && (
        <div className="nx-card nx-tempcred">
          <strong>Senha temporária para {tempCred.email}:</strong>
          <code className="mono nx-tempcred__code">{tempCred.password}</code>
          <p
            style={{
              color: "var(--nx-mute)",
              margin: "4px 0 0",
              fontSize: ".8125rem",
            }}
          >
            Copie e envie ao usuário por canal seguro. Esta senha só aparece
            agora.
          </p>
          <button
            type="button"
            className="nx-btn nx-btn--sm nx-btn--ghost-dark"
            onClick={() => setTempCred(null)}
            style={{ marginTop: "var(--nx-space-3)" }}
          >
            Ok, guardei
          </button>
        </div>
      )}

      {showForm && (
        <div className="nx-card" style={{ marginBottom: "var(--nx-space-4)" }}>
          <form onSubmit={create}>
            <div className="nx-filters">
              <label className="nx-field">
                <span>E-mail</span>
                <input name="email" type="email" required />
              </label>
              <label className="nx-field">
                <span>Nome de exibição</span>
                <input name="displayName" type="text" required />
              </label>
              <label className="nx-field">
                <span>Perfil</span>
                <select name="role" defaultValue="viewer">
                  {(Object.keys(ROLE_LABELS) as RoleSlug[]).map((r) => (
                    <option key={r} value={r}>
                      {ROLE_LABELS[r]}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <fieldset style={{ marginTop: "var(--nx-space-4)", border: 0, padding: 0 }}>
              <legend className="nx-field" style={{ marginBottom: "var(--nx-space-2)" }}>
                <span>Acesso por unidade</span>
              </legend>
              <label className="nx-field" style={{ maxWidth: 260 }}>
                <select
                  value={newGarageScope}
                  onChange={(e) => setNewGarageScope(e.target.value as "all" | "restricted")}
                >
                  <option value="all">Todas as unidades</option>
                  <option value="restricted">Unidades selecionadas</option>
                </select>
              </label>
              {newGarageScope === "restricted" && (
                <div className="nx-filters" style={{ marginTop: "var(--nx-space-3)" }}>
                  {garages.map((garage) => (
                    <label key={garage.id} className="nx-check">
                      <input
                        type="checkbox"
                        checked={newGarageIds.includes(garage.id)}
                        onChange={(e) => toggleNewGarage(garage.id, e.target.checked)}
                      />
                      <span>{garage.name} ({garage.code})</span>
                    </label>
                  ))}
                </div>
              )}
            </fieldset>
            <button
              className="nx-btn"
              type="submit"
              style={{ marginTop: "var(--nx-space-4)" }}
            >
              Criar
            </button>
          </form>
        </div>
      )}

      {error && (
        <div className="nx-error" style={{ marginBottom: "var(--nx-space-4)" }}>
          {error}
        </div>
      )}

      <div className="nx-card">
        <div className="nx-table-wrap">
          <table className="nx-table">
            <thead>
              <tr>
                <th>E-mail</th>
                <th>Nome</th>
                <th>Perfil</th>
                <th>Status</th>
                <th>Unidades</th>
                <th>Último login</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id}>
                  <td className="mono">
                    {u.email}
                    {u.mustChangePassword && (
                      <span
                        className="nx-badge nx-badge--charging"
                        style={{ marginLeft: 8 }}
                        title="Senha temporária pendente de troca"
                      >
                        troca pendente
                      </span>
                    )}
                  </td>
                  <td>{u.displayName}</td>
                  <td>
                    <select
                      value={u.role}
                      onChange={(e) =>
                        void patch(u.id, { role: e.target.value })
                      }
                      disabled={u.id === me?.id}
                      style={{ padding: "4px 8px" }}
                    >
                      {(Object.keys(ROLE_LABELS) as RoleSlug[]).map((r) => (
                        <option key={r} value={r}>
                          {ROLE_LABELS[r]}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    {u.role === "admin" ? (
                      <span> Todas as unidades </span>
                    ) : (
                      <>
                        <select
                          value={u.garageScope}
                          onChange={(e) => void updateUnitScope(u, e.target.value as "all" | "restricted")}
                          style={{ padding: "4px 8px", maxWidth: 180 }}
                        >
                          <option value="all">Todas</option>
                          <option value="restricted">Selecionadas</option>
                        </select>
                        {u.garageScope === "restricted" && (
                          <details style={{ marginTop: 6 }}>
                            <summary title={unitLabel(u)}>{unitLabel(u)}</summary>
                            <div style={{ display: "grid", gap: 4, marginTop: 6 }}>
                              {garages.map((garage) => (
                                <label key={garage.id} className="nx-check">
                                  <input
                                    type="checkbox"
                                    checked={u.garageIds.includes(garage.id)}
                                    onChange={(e) => void toggleGarage(u, garage.id, e.target.checked)}
                                  />
                                  <span>{garage.name} ({garage.code})</span>
                                </label>
                              ))}
                            </div>
                          </details>
                        )}
                      </>
                    )}
                  </td>
                  <td>
                    <span
                      className={
                        u.active
                          ? "nx-badge nx-badge--available"
                          : "nx-badge nx-badge--fault"
                      }
                    >
                      {u.active ? "ativo" : "inativo"}
                    </span>
                  </td>
                  <td>{fmt(u.lastLoginAt)}</td>
                  <td>
                    <button
                      type="button"
                      className="nx-btn nx-btn--sm nx-btn--ghost-dark"
                      onClick={() => void resetPwd(u)}
                    >
                      Reset senha
                    </button>{" "}
                    <button
                      type="button"
                      className="nx-btn nx-btn--sm nx-btn--ghost-dark"
                      onClick={() => void toggleActive(u)}
                      disabled={u.id === me?.id}
                    >
                      {u.active ? "Desativar" : "Ativar"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
